import type { LocaleOption, SupportedLocale } from '@/types/i18n'

export const DEFAULT_LOCALE: SupportedLocale = 'en'

// Keyed by SupportedLocale so adding a locale to the union without a label here
// is a compile error rather than a blank entry in the selector.
const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  fr: 'Français',
  es: 'Español',
  de: 'Deutsch',
  pt: 'Português'
}

export const SUPPORTED_LOCALES = Object.keys(LOCALE_LABELS) as SupportedLocale[]

export const LOCALE_OPTIONS: LocaleOption[] = SUPPORTED_LOCALES.map((code) => ({
  code,
  label: LOCALE_LABELS[code]
}))

export const isSupportedLocale = (value: unknown): value is SupportedLocale =>
  typeof value === 'string' && Object.prototype.hasOwnProperty.call(LOCALE_LABELS, value)

/**
 * Reduce an arbitrary language tag to a supported locale, falling back to
 * English. Region variants and casing are tolerated ("pt-BR", "DE", "en_US")
 * because tags reach us from browser settings and from ClassyVue, which echoes
 * back whatever the embed URL carried.
 */
export const normalizeLocale = (value: string | null | undefined): SupportedLocale => {
  const base = value?.trim().split(/[-_]/)[0]?.toLowerCase()
  return isSupportedLocale(base) ? base : DEFAULT_LOCALE
}
